import React, { useState, useRef, useEffect } from "react";
import { List, EditButton, DeleteButton, useDataGrid } from "@refinedev/mui";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import {
  IResourceComponentsProps,
  useTranslate,
} from "@refinedev/core";
import {
  Box,
  Chip,
  Grid,
  IconButton,
  Tooltip,
} from "@mui/material";
import { MapContainer, TileLayer, Marker, Circle, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Navigation, RefreshCw } from "lucide-react";
import { useIPLocation } from "../../hooks/useIPLocation";

// Fix pour les icônes Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

// Component to handle map initialization
function MapInitializer({ places, fitMapToAllPlaces }: { places: readonly any[], fitMapToAllPlaces: () => void }) {
  const map = useMapEvents({});
  const [hasInitialized, setHasInitialized] = React.useState(false);

  React.useEffect(() => {
    if (places.length > 0 && map && !hasInitialized) {
      // Wait for map to be ready, then fit bounds (only once on mount)
      map.whenReady(() => {
        setTimeout(() => {
          fitMapToAllPlaces();
          setHasInitialized(true);
        }, 100);
      });
    }
  }, [places.length, map, fitMapToAllPlaces, hasInitialized]);

  return null;
}

export const SharedPlacesList: React.FC<IResourceComponentsProps> = () => {
  const translate = useTranslate();
  const { location: ipLocation } = useIPLocation();
  const { dataGridProps } = useDataGrid({
    resource: "places",
    initialPageSize: 10,
    pagination: { mode: "client" },
  });

  const [selectedPlace, setSelectedPlace] = useState<any>(null);
  const mapRef = useRef<any>(null);

  // Get all places from dataGridProps
  const places = dataGridProps.rows || [];

  // Create custom emoji icon with circle background and pointer
  const createEmojiIcon = (emoji: string, isSelected: boolean) => {
    const size = isSelected ? 50 : 44;
    const fontSize = isSelected ? '1.6rem' : '1.4rem';
    const borderColor = isSelected ? '#1976d2' : '#666666';
    const shadowColor = isSelected ? "rgba(25, 118, 210, 0.4)" : "#666666";

    return L.divIcon({
      html: `
        <div style="position: relative; width: ${size}px; height: ${size + 12}px;">
          <div style="
            width: ${size}px;
            height: ${size}px;
            background: white;
            border: 3px solid ${borderColor};
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: ${fontSize};
            box-shadow: 0 2px 8px ${shadowColor};
            position: relative;
            z-index: 2;
          ">
            ${emoji || '📍'}
          </div>
          <div style="
            position: absolute;
            bottom: 2px;
            left: 50%;
            transform: translateX(-50%);
            width: 0;
            height: 0;
            border-left: 8px solid transparent;
            border-right: 8px solid transparent;
            border-top: 12px solid ${borderColor};
            z-index: 3;
          "></div>
        </div>
      `,
      className: 'emoji-marker',
      iconSize: [size, size + 12],
      iconAnchor: [size / 2, size + 12],
      popupAnchor: [0, -(size + 12)]
    });
  };

  // Calculate bounds to fit all places with their radius circles
  const fitMapToAllPlaces = () => {
    if (places.length === 0 || !mapRef.current) return;

    const map = mapRef.current;

    if (places.length === 1) {
      // If only one place, center on it with zoom that shows the radius
      const place = places[0];
      const radius = place.radius || 100;
      // Calculate appropriate zoom level based on radius (approximate)
      const zoom = radius > 500 ? 13 : radius > 200 ? 14 : 15;
      map.setView([place.latitude, place.longitude], zoom);
    } else {
      // If multiple places, calculate bounds including radius circles
      const boundsPoints: [number, number][] = [];

      places.forEach((place: any) => {
        const radius = place.radius || 100;
        const lat = place.latitude;
        const lon = place.longitude;

        // Calculate approximate bounds for the circle
        // 1 degree of latitude ≈ 111km
        const latOffset = (radius / 111000);
        // 1 degree of longitude varies by latitude
        const lonOffset = (radius / (111000 * Math.cos(lat * Math.PI / 180)));

        // Add circle boundary points
        boundsPoints.push([lat + latOffset, lon]);
        boundsPoints.push([lat - latOffset, lon]);
        boundsPoints.push([lat, lon + lonOffset]);
        boundsPoints.push([lat, lon - lonOffset]);
      });

      const bounds = L.latLngBounds(boundsPoints);
      map.fitBounds(bounds, { padding: [80, 80], maxZoom: 15 });
    }
  };

  // Center map on selected place
  useEffect(() => {
    if (selectedPlace && mapRef.current) {
      const map = mapRef.current;
      map.setView([selectedPlace.latitude, selectedPlace.longitude], 15);
    }
  }, [selectedPlace]);

  // Reset selection on initial load
  useEffect(() => {
    if (places.length > 0) {
      setSelectedPlace(null);
    }
  }, [places.length]);

  const columns = React.useMemo<GridColDef[]>(
    () => [
      {
        field: "name",
        flex: 1,
        headerName: translate("places.fields.name"),
        minWidth: 200,
        renderCell: function render({ row }) {
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <span style={{ fontSize: "1.5rem" }}>{row.emoji || "📍"}</span>
              <span>{row.name}</span>
            </Box>
          );
        },
      },
      {
        field: "isActive",
        headerName: translate("places.fields.status"),
        width: 100,
        flex: 0,
        renderCell: function render({ value }) {
          return (
            <Chip
              label={translate(value ? "places.status.active" : "places.status.inactive")}
              color={value ? "success" : "default"}
              size="small"
            />
          );
        },
      },
      {
        field: "actions",
        headerName: translate("table.actions"),
        sortable: false,
        renderCell: function render({ row }) {
          return (
            <Box sx={{ display: "flex", gap: 1 }}>
              <Tooltip title={translate("places.actions.centerMap", "Centrer sur la carte")}>
                <IconButton
                  size="small"
                  onClick={() => setSelectedPlace(row)}
                  sx={{ color: "primary.main" }}
                >
                  <Navigation size={18} />
                </IconButton>
              </Tooltip>
              <EditButton hideText recordItemId={row.id} />
              <DeleteButton hideText recordItemId={row.id} />
            </Box>
          );
        },
        align: "center",
        headerAlign: "center",
        width: 150,
        flex: 0,
      },
    ],
    [translate]
  );

  return (
    <List>
      <Grid container spacing={2} sx={{ height: 'calc(100vh - 142px)' }}>
        <Grid item xs={12} md={4} sx={{ height: '100%', overflow: 'auto' }}>
          <DataGrid
            {...dataGridProps}
            columns={columns}
            autoHeight
            filterModel={undefined}
            disableColumnMenu={false}
            columnVisibilityModel={{
              name: true,
              isActive: true,
              actions: true,
            }}
            sx={{ minWidth: 470 }}
          />
        </Grid>
        <Grid item xs={12} md={8} sx={{ height: '100%' }}>
          <Box
            sx={{ height: "100%", width: "100%", position: "sticky", top: 16 }}
          >
            {places.length > 0 || ipLocation ? (
              <Box sx={{ position: "relative", height: "100%", width: "100%" }}>
                <MapContainer
                  center={
                    places.length > 0
                      ? [places[0].latitude, places[0].longitude]
                      : ipLocation
                      ? [ipLocation.latitude, ipLocation.longitude]
                      : [43.2962717, 5.3832961]
                  }
                  zoom={1}
                  style={{ height: "100%", width: "100%", borderRadius: "8px" }}
                  ref={mapRef}
                >
                  <MapInitializer
                    places={places}
                    fitMapToAllPlaces={fitMapToAllPlaces}
                  />
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  {places.map((place: any) => (
                    <React.Fragment key={place.id}>
                      <Marker
                        position={[place.latitude, place.longitude]}
                        icon={createEmojiIcon(
                          place.emoji,
                          selectedPlace?.id === place.id
                        )}
                      />
                      <Circle
                        center={[place.latitude, place.longitude]}
                        radius={place.radius || 100}
                        pathOptions={{
                          color:
                            selectedPlace?.id === place.id
                              ? "#1976d2"
                              : "#666666",
                          fillColor:
                            selectedPlace?.id === place.id
                              ? "#1976d2"
                              : "#666666",
                          fillOpacity: 0.2,
                        }}
                      />
                    </React.Fragment>
                  ))}
                </MapContainer>
                <Tooltip
                  title={translate(
                    "places.actions.resetView",
                    "Réinitialiser la vue"
                  )}
                >
                  <IconButton
                    sx={{
                      position: "absolute",
                      top: 10,
                      right: 10,
                      zIndex: 1000,
                      bgcolor: "white",
                      color: "black",
                      boxShadow: 2,
                      "&:hover": {
                        bgcolor: "grey.100",
                      },
                    }}
                    onClick={() => {
                      setSelectedPlace(null);
                      fitMapToAllPlaces();
                    }}
                    size="small"
                  >
                    <RefreshCw size={18} />
                  </IconButton>
                </Tooltip>
              </Box>
            ) : (
              <Box
                sx={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "grey.100",
                  borderRadius: "8px",
                }}
              >
                {translate("places.noPlaces", "Aucun lieu à afficher")}
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </List>
  );
};
